/**
 * A person's own board preferences: how the board is laid out when they open
 * it. Stored on the server rather than in the browser so they follow the person
 * from the laptop to the phone app, and parsed here so the server and the UI
 * agree on what a valid value is.
 *
 * Every field has a default and an unknown or malformed value falls back to it,
 * never an error: a stale tab sending a setting that has since been dropped
 * must not stop the rest from saving.
 */
export const SETTINGS_VIEWS = ["board", "timeline", "calendar", "messages", "shopping", "money"] as const;
/** Every view the switcher lists, in its order. The web client's `VIEWS` is this list.
 *  `team` is the team's Settings, last, offered only to its admins (shared/teamViews.ts). */
export const VIEWS = ["board", "timeline", "calendar", "messages", "shopping", "money", "repos", "automation", "tmux", "team"] as const;
export type SwitcherView = (typeof VIEWS)[number];

/**
 * Something pinned to the topbar as a pill: a task's drawer, a tmux session's
 * terminal, or a queued Claude request's prompt. Views are not pinned — the
 * switcher is one click away — and an old `view` pin is dropped as malformed. Only what it
 * takes to find it again — a pill's label is read live (a task's title, the
 * session's task), so a rename does not leave a stale pin behind.
 */
export type Pin =
  /** The Global settings view: not in the switcher (an admin reaches it from the profile), so a kind of its own. */
  | { kind: "settings" }
  | { kind: "task"; id: string }
  /** `auto`: pinned by lib/autoPin.ts while its Claude waits for an answer, and
   *  unpinned by it once answered. One the reader pinned has none, and stays. */
  | { kind: "session"; name: string; taskId: string | null; auto?: true }
  | { kind: "queued"; id: string; taskId: string }
  /** The file editor over a checkout (`worktree` null) or a worktree, with `path` open, or just its tree. */
  | { kind: "files"; repo: string; worktree: string | null; path: string | null };

/** One pin's identity: pinning something already pinned unpins it. */
export function pinKey(p: Pin): string {
  switch (p.kind) {
    case "settings": return "settings";
    case "task": return `task:${p.id}`;
    case "session": return `session:${p.name}`;
    case "queued": return `queued:${p.id}`;
    case "files": return `files:${p.repo}\0${p.worktree ?? ""}\0${p.path ?? ""}`;
  }
}

const MAX_PINS = 50;
/** `system` follows the device's light/dark setting; the others override it. */
export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export interface UserSettings {
  /** What is on screen when the board opens. */
  startView: (typeof SETTINGS_VIEWS)[number];
  hideDone: boolean;
  hideDoneSubtasks: boolean;
  commentsOnly: boolean;
  /** The board shows only the reader's own tasks, and the parents they sit in. */
  onlyMine: boolean;
  sortBy: "manual" | "urgency";
  /**
   * The repo headings, in the order this person dragged them into. `""` is the
   * "no repo" heading. A repo not listed goes after the listed ones — see
   * `compareRepos()`.
   */
  repoOrder: string[];
  /**
   * The team whose board, messages and calendar are on screen, by id, or
   * `"personal"` for the person's own tasks. `""`, or a team the person has
   * since left, means their first team.
   */
  team: string;
  theme: Theme;
  /**
   * What is pinned to the topbar as pills, beside the switcher, in the order it
   * was pinned. Kept even when the board on screen does not offer it (Messages
   * on Personal, tmux off a coding board): the pill just does not show there.
   */
  pins: Pin[];
}

export const DEFAULT_SETTINGS: UserSettings = {
  startView: "board",
  hideDone: true,
  hideDoneSubtasks: false,
  commentsOnly: true,
  onlyMine: false,
  sortBy: "manual",
  repoOrder: [],
  team: "",
  theme: "system",
  pins: [],
};

const MAX_REPOS = 500;
const MAX_REPO_NAME = 200;

const oneOf = <T extends string>(allowed: readonly T[], v: unknown, fallback: T): T =>
  allowed.includes(v as T) ? (v as T) : fallback;
function repoList(v: unknown, fallback: string[]): string[] {
  if (!Array.isArray(v) || v.length > MAX_REPOS) return fallback;
  if (!v.every((r) => typeof r === "string" && r.length <= MAX_REPO_NAME)) return fallback;
  return [...new Set(v as string[])];
}
const shortId = (v: unknown, max = 200): v is string => typeof v === "string" && v.length > 0 && v.length <= max;

function parsePin(v: unknown): Pin | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  switch (o.kind) {
    case "settings":
      return { kind: "settings" };
    case "task":
      return shortId(o.id, 64) ? { kind: "task", id: o.id } : null;
    case "session":
      if (!shortId(o.name) || !(o.taskId === null || shortId(o.taskId, 64))) return null;
      return o.auto === true ? { kind: "session", name: o.name, taskId: o.taskId, auto: true } : { kind: "session", name: o.name, taskId: o.taskId };
    case "queued":
      return shortId(o.id, 64) && shortId(o.taskId, 64) ? { kind: "queued", id: o.id, taskId: o.taskId } : null;
    case "files":
      if (!shortId(o.repo) || !(o.worktree === null || shortId(o.worktree)) || !(o.path === null || shortId(o.path, 1024))) return null;
      return { kind: "files", repo: o.repo, worktree: o.worktree, path: o.path };
    default:
      return null;
  }
}

/** A malformed list is ignored whole; a malformed pin in a good one is dropped,
 *  as is a second of the same thing. */
function pinList(v: unknown, fallback: Pin[]): Pin[] {
  if (!Array.isArray(v) || v.length > MAX_PINS) return fallback;
  const seen = new Set<string>();
  const out: Pin[] = [];
  for (const raw of v) {
    const pin = parsePin(raw);
    if (!pin || seen.has(pinKey(pin))) continue;
    seen.add(pinKey(pin));
    out.push(pin);
  }
  return out;
}
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const id = (v: unknown, fallback: string): string => (typeof v === "string" && v.length <= 64 ? v : fallback);

/** Lay `input` over `base`, keeping only the fields that are valid. */
export function parseSettings(input: unknown, base: UserSettings = DEFAULT_SETTINGS): UserSettings {
  const o = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  return {
    startView: oneOf(SETTINGS_VIEWS, o.startView, base.startView),
    hideDone: bool(o.hideDone, base.hideDone),
    hideDoneSubtasks: bool(o.hideDoneSubtasks, base.hideDoneSubtasks),
    commentsOnly: bool(o.commentsOnly, base.commentsOnly),
    onlyMine: bool(o.onlyMine, base.onlyMine),
    sortBy: oneOf(["manual", "urgency"] as const, o.sortBy, base.sortBy),
    repoOrder: repoList(o.repoOrder, base.repoOrder),
    team: id(o.team, base.team),
    theme: oneOf(THEMES, o.theme, base.theme),
    pins: pinList(o.pins, base.pins),
  };
}

/**
 * Heading order for repos (`null` = "no repo"): the ones in `order` as placed
 * there, then the rest alphabetically, with "no repo" last unless it was
 * placed. A repo that appears later lands in the alphabetical tail rather than
 * shuffling what someone already arranged.
 */
export function compareRepos(order: readonly string[]): (a: string | null, b: string | null) => number {
  const at = new Map(order.map((r, i) => [r, i]));
  return (a, b) => {
    const ia = at.get(a ?? "");
    const ib = at.get(b ?? "");
    if (ia !== undefined || ib !== undefined) return (ia ?? Infinity) - (ib ?? Infinity);
    if (a === null) return b === null ? 0 : 1;
    if (b === null) return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  };
}

/**
 * The saved order after dragging `from` onto `to`'s place: it takes that slot,
 * landing after `to` when it moved down and before it when it moved up.
 * `present` is every repo on the board, so the result places all of them and a
 * later move never reshuffles the ones that were only sorted by name.
 */
export function moveRepo(
  order: readonly string[],
  present: readonly (string | null)[],
  from: string | null,
  to: string | null,
): string[] {
  const all = [...new Set([...order, ...present.map((r) => r ?? "")])]
    .map((r) => (r === "" ? null : r))
    .sort(compareRepos(order))
    .map((r) => r ?? "");
  const src = all.indexOf(from ?? "");
  const dst = all.indexOf(to ?? "");
  if (src < 0 || dst < 0 || src === dst) return [...order];
  all.splice(src, 1);
  // With `from` gone, index `dst` is just after `to` when it sat above `to`,
  // and `to`'s own slot when it sat below.
  all.splice(dst, 0, from ?? "");
  return all;
}
